/**
 * Timeline Form Validator
 * Handles form validation for timeline items
 */
class TimelineFormValidator {
  constructor() {
    this.rules = {
      'item-title': {
        required: true,
        minLength: 1,
        maxLength: 150,
        message: 'Title is required and must be between 1-150 characters'
      },
      'item-start-date': {
        required: true,
        type: 'date',
        message: 'Start date is required'
      },
      'item-duration': {
        required: true,
        type: 'number',
        min: 1,
        max: 365,
        message: 'Duration must be between 1-365 days'
      },
      'item-progress': {
        required: false,
        type: 'number',
        min: 0,
        max: 100,
        message: 'Progress must be between 0-100'
      }
    };
  }

  validate(formData) {
    const errors = [];

    // Check each rule
    for (const [fieldId, rule] of Object.entries(this.rules)) {
      const value = formData[fieldId.replace('item-', '').replace('-', '_')];
      const error = this.validateField(fieldId, value, rule);
      if (error) {
        errors.push(error);
      }
    }

    // Custom validations
    const customErrors = this.customValidations(formData);
    errors.push(...customErrors);

    return {
      isValid: errors.length === 0,
      errors: errors,
      firstErrorField: errors.length > 0 ? errors[0].field : null
    };
  }

  validateField(fieldId, value, rule) {
    // Required validation
    if (rule.required && (!value || value.toString().trim() === '')) {
      return {
        field: fieldId,
        message: rule.message || `${fieldId} is required`
      };
    }

    // Skip other validations if value is empty and not required
    if (!value && !rule.required) {
      return null;
    }

    // Type validation
    if (rule.type === 'number') {
      const numValue = Number(value);
      if (isNaN(numValue)) {
        return {
          field: fieldId,
          message: `${fieldId} must be a valid number`
        };
      }

      // Min/Max validation for numbers
      if (rule.min !== undefined && numValue < rule.min) {
        return {
          field: fieldId,
          message: rule.message || `${fieldId} must be at least ${rule.min}`
        };
      }

      if (rule.max !== undefined && numValue > rule.max) {
        return {
          field: fieldId,
          message: rule.message || `${fieldId} must be at most ${rule.max}`
        };
      }
    }

    // Date validation
    if (rule.type === 'date') {
      const dateValue = new Date(value);
      if (isNaN(dateValue.getTime())) {
        return {
          field: fieldId,
          message: rule.message || `${fieldId} must be a valid date`
        };
      }
    }

    // Length validation
    if (rule.minLength !== undefined && value.length < rule.minLength) {
      return {
        field: fieldId,
        message: rule.message || `${fieldId} must be at least ${rule.minLength} characters`
      };
    }

    if (rule.maxLength !== undefined && value.length > rule.maxLength) {
      return {
        field: fieldId,
        message: rule.message || `${fieldId} must be at most ${rule.maxLength} characters`
      };
    }

    return null;
  }

  customValidations(formData) {
    const errors = [];

    // Validate start date is not in the past (for new items)
    if (formData.start_date && !formData.id) {
      const startDate = new Date(formData.start_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate < today) {
        errors.push({
          field: 'item-start-date',
          message: 'Start date cannot be in the past'
        });
      }
    }

    // Validate color format
    if (formData.color && !/^#[0-9A-F]{6}$/i.test(formData.color)) {
      errors.push({
        field: 'item-color',
        message: 'Color must be in valid hex format (#RRGGBB)'
      });
    }

    return errors;
  }

  showErrors(errors) {
    // Clear previous errors
    this.clearErrors();

    if (errors.length === 0) return;

    // Show first error and focus field
    const firstError = errors[0];
    const field = document.getElementById(firstError.field);

    if (field) {
      field.focus();
      this.showFieldError(field, firstError.message);
    }

    // Show summary if multiple errors
    if (errors.length > 1) {
      const errorMessages = errors.map(e => e.message).join('\n');
      alert(`Found ${errors.length} validation errors:\n\n${errorMessages}`);
    } else {
      alert(firstError.message);
    }
  }

  showFieldError(field, message) {
    // Add error class
    field.classList.add('validation-error');

    // Create error message element
    const errorElement = document.createElement('div');
    errorElement.className = 'field-error-message';
    errorElement.textContent = message;

    // Insert after field
    field.parentNode.insertBefore(errorElement, field.nextSibling);

    // Remove error on input
    const removeError = () => {
      field.classList.remove('validation-error');
      if (errorElement.parentNode) {
        errorElement.parentNode.removeChild(errorElement);
      }
      field.removeEventListener('input', removeError);
      field.removeEventListener('change', removeError);
    };

    field.addEventListener('input', removeError);
    field.addEventListener('change', removeError);
  }

  clearErrors() {
    // Remove error classes
    const errorFields = document.querySelectorAll('.validation-error');
    errorFields.forEach(field => {
      field.classList.remove('validation-error');
    });

    // Remove error messages
    const errorMessages = document.querySelectorAll('.field-error-message');
    errorMessages.forEach(message => {
      if (message.parentNode) {
        message.parentNode.removeChild(message);
      }
    });
  }
}

// Add CSS for validation errors
const validationStyle = document.createElement('style');
validationStyle.textContent = `
    .validation-error {
        border-color: #ff4757 !important;
        box-shadow: 0 0 0 2px rgba(255, 71, 87, 0.1) !important;
    }

    .field-error-message {
        color: #ff4757;
        font-size: 12px;
        margin-top: 4px;
        display: block;
    }
`;
document.head.appendChild(validationStyle);

// Export for use in other scripts
if (typeof window !== 'undefined') {
  window.TimelineFormValidator = TimelineFormValidator;
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = TimelineFormValidator;
}

window.TimelineFormValidator = TimelineFormValidator;
